import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, ref } from 'vue';
import { z } from 'zod';

import useSearchParamsState from '~/hooks/useSearchParamsState';

import type { GetCessionsRequest, OperationType } from '../services/capitalMarketsService';

import cessionFiltersSchema from '../schemas/filtersSchema';

export type UseCessionParams = {
  date_end?: string;
  date_start?: string;
  page?: number;
  page_size?: number;
  search_query?: string;
  search_type?: string;
  status?: string;
  type?: string | string[];
};

type CessionFiltersForm = z.infer<typeof cessionFiltersSchema>;

const INITIAL_FILTER_STATE: UseCessionParams = {
  date_end: undefined,
  date_start: undefined,
  page: 1,
  page_size: 10,
  search_query: undefined,
  search_type: undefined,
  status: undefined,
  type: undefined,
};

export default function useCessionFilters(options?: {
  onFiltersChange?: () => void;
}) {
  const searchParams = useSearchParamsState<UseCessionParams>(
    INITIAL_FILTER_STATE,
    { parseArrays: false },
  );

  const normalizeType = (type: string | string[] | undefined): string | undefined => {
    if (!type) return undefined;
    return Array.isArray(type) ? type[0] : type;
  };

  const localDateRange = ref<[string, string] | undefined>(
    searchParams.value.date_start && searchParams.value.date_end
      ? [searchParams.value.date_start, searchParams.value.date_end]
      : undefined,
  );

  const dateRange = computed({
    get: () => localDateRange.value,
    set: (value: [string, string] | undefined) => {
      localDateRange.value = value;
    },
  });

  const filterForm = useForm<CessionFiltersForm>({
    initialValues: {
      date_end: searchParams.value.date_end || '',
      date_start: searchParams.value.date_start || '',
      search_query: searchParams.value.search_query || '',
      search_type: searchParams.value.search_type || undefined,
      status: searchParams.value.status || undefined,
      type: normalizeType(searchParams.value.type),
    },
    validationSchema: toTypedSchema(cessionFiltersSchema),
  });

  const [searchQueryModel, searchQueryProps] = filterForm.defineField('search_query');
  const [searchTypeModel, searchTypeProps] = filterForm.defineField('search_type');
  const [statusModel, statusProps] = filterForm.defineField('status');
  const [typeModel, typeProps] = filterForm.defineField('type');

  const apiParams = computed<GetCessionsRequest>(() => {
    const params = searchParams.value;
    const mappedParams: GetCessionsRequest = {};

    if (params.page) {
      mappedParams.page = Number(params.page);
    }
    if (params.page_size) {
      mappedParams.page_size = Number(params.page_size);
    }
    if (params.type) {
      const typeValue = Array.isArray(params.type) ? params.type[0] : params.type;
      if (typeValue && typeValue.trim()) {
        mappedParams.operation_types = [typeValue as OperationType];
      }
    } else {
      mappedParams.operation_types = ['new_cession', 'overturning'];
    }
    if (params.date_start?.trim()) {
      mappedParams.start_date = params.date_start;
    }
    if (params.date_end?.trim()) {
      mappedParams.end_date = params.date_end;
    }
    if (params.status?.trim()) {
      mappedParams.status_param = params.status as GetCessionsRequest['status_param'];
    }
    if (params.search_type?.trim()) {
      mappedParams.search_type = params.search_type as GetCessionsRequest['search_type'];
    }
    if (params.search_query?.trim()) {
      mappedParams.search_query = params.search_query.trim();
    }

    return mappedParams;
  });

  function handleSearch(): void {
    if (dateRange.value && dateRange.value.length === 2) {
      const [start, end] = dateRange.value;
      if (new Date(start) > new Date(end)) {
        return;
      }
    }

    searchParams.value = {
      ...searchParams.value,
      date_end: dateRange.value?.[1] || undefined,
      date_start: dateRange.value?.[0] || undefined,
      page: 1,
      search_query: searchQueryModel.value?.trim() || undefined,
      search_type: searchTypeModel.value || undefined,
      status: statusModel.value || undefined,
      type: typeModel.value || undefined,
    };

    if (options?.onFiltersChange) {
      options.onFiltersChange();
    }
  }

  function handleReset(): void {
    searchParams.value = { ...INITIAL_FILTER_STATE };
    localDateRange.value = undefined;
    filterForm.resetForm({
      values: {
        date_end: '',
        date_start: '',
        search_query: '',
        search_type: undefined,
        status: undefined,
        type: undefined,
      },
    });
  }

  const updateSearchParams = (updates: Partial<UseCessionParams>): void => {
    searchParams.value = { ...searchParams.value, ...updates };
  };

  function setPage(value: number): void {
    updateSearchParams({ page: Number(value) });
  }

  function setPageSize(value: number): void {
    updateSearchParams({
      page: 1,
      page_size: Number(value),
    });
  }

  function initializeForm(): void {
    filterForm.setValues({
      date_end: searchParams.value.date_end || '',
      date_start: searchParams.value.date_start || '',
      search_query: searchParams.value.search_query || '',
      search_type: searchParams.value.search_type || undefined,
      status: searchParams.value.status || undefined,
      type: normalizeType(searchParams.value.type),
    });

    if (searchParams.value.date_start && searchParams.value.date_end) {
      localDateRange.value = [searchParams.value.date_start, searchParams.value.date_end];
    }
  }

  return {
    apiParams,
    dateRange,
    filterForm,
    handleReset,
    handleSearch,
    initializeForm,
    searchParams,
    searchQueryModel,
    searchQueryProps,
    searchTypeModel,
    searchTypeProps,
    setPage,
    setPageSize,
    statusModel,
    statusProps,
    typeModel,
    typeProps,
  };
}
